import { useState, useEffect, useCallback } from "react";
import { useLoading } from "../../../../../../hooks/useLoading";
import { getEntriToken, IDnsEntriesResponse } from "../../../../../../utils/fetch";
import { getDnsDelegateEntriToken } from "../../../../../../utils/dnsDelegateApi";
import entri from "entrijs";

export type DnsEntriMode = "admin" | "delegate";

interface UseEntriModalProps {
  domainData: IDnsEntriesResponse | null;
  isFetchingDns: boolean;
  adminUrl?: string;
  charityId?: string;
  serviceAccessToken?: string;
  delegateToken?: string;
  mode?: DnsEntriMode;
  onSuccess?: () => void;
  onAuthorizationSuccess?: () => void | Promise<void>;
  onAutomaticEntriSuccess?: () => void | Promise<void>;
  autoLaunch?: boolean;
}

export const useEntriModal = ({
  domainData,
  isFetchingDns,
  adminUrl,
  charityId,
  serviceAccessToken,
  delegateToken,
  mode = "admin",
  onSuccess,
  onAuthorizationSuccess,
  onAutomaticEntriSuccess,
  autoLaunch = true,
}: UseEntriModalProps) => {
  const [entriCredentials, setEntriCredentials] = useState<{ authToken: string; applicationId: string } | null>(null);
  const [entriLaunched, setEntriLaunched] = useState<boolean>(false);
  const { loading: isLoadingEntri, runWithLoading } = useLoading(false);

  const transformDnsToEntriFormat = useCallback((domainData: IDnsEntriesResponse) => {
    return Object.values(domainData.dns).map((entry) => ({
      type: entry.type.toUpperCase(),
      host: entry.host,
      value: entry.data,
      ttl: 300,
      ...(entry.type.toUpperCase() === "MX" && { priority: 10 }),
    }));
  }, []);

  const launchEntriModal = useCallback(async () => {
    const canLaunchAdmin = mode === "admin" && adminUrl && charityId && serviceAccessToken;
    const canLaunchDelegate = mode === "delegate" && adminUrl && delegateToken;

    if (!domainData || entriLaunched || (!canLaunchAdmin && !canLaunchDelegate)) {
      return;
    }

    setEntriLaunched(true);

    await runWithLoading(async () => {
      try {
        let credentials = entriCredentials;
        if (!credentials) {
          if (mode === "delegate" && adminUrl && delegateToken) {
            credentials = await getDnsDelegateEntriToken(adminUrl, delegateToken);
          } else if (adminUrl && charityId && serviceAccessToken) {
            credentials = await getEntriToken(adminUrl, charityId, serviceAccessToken);
          }
          if (credentials) {
            setEntriCredentials(credentials);
          }
        }

        if (credentials) {
          const entriDnsRecords = transformDnsToEntriFormat(domainData);

          const entriConfig = {
            applicationId: credentials.applicationId,
            token: credentials.authToken,
            dnsRecords: entriDnsRecords,
            prefilledDomain: domainData.domain,
          };

          const handleOnEntriStepChange = (event: any) => {
            if (event.detail.step === "MANUAL_CONFIGURATION") {
              entri.close();
            }
          };

          const handleOnEntriClose = async (event: any) => {
            if (event.detail.success && event.detail.setupType === "automatic") {
              if (onAutomaticEntriSuccess) {
                await onAutomaticEntriSuccess();
              } else if (onAuthorizationSuccess) {
                await onAuthorizationSuccess();
              } else if (onSuccess) {
                onSuccess();
              }
            }
            window.removeEventListener("onEntriClose", handleOnEntriClose, false);
            window.removeEventListener("onEntriStepChange", handleOnEntriStepChange, false);
          };

          window.addEventListener("onEntriStepChange", handleOnEntriStepChange, false);
          window.addEventListener("onEntriClose", handleOnEntriClose, false);

          await entri.showEntri(entriConfig);
        }
      } catch {
        // Keep entriLaunched true so auto-launch does not retry and burn API quota.
      }
    });
  }, [
    domainData,
    entriLaunched,
    entriCredentials,
    adminUrl,
    charityId,
    serviceAccessToken,
    delegateToken,
    mode,
    onSuccess,
    onAuthorizationSuccess,
    onAutomaticEntriSuccess,
    runWithLoading,
    transformDnsToEntriFormat,
  ]);

  useEffect(() => {
    if (autoLaunch && domainData && !domainData.valid && !entriLaunched && !isFetchingDns) {
      launchEntriModal();
    }
  }, [autoLaunch, domainData, entriLaunched, isFetchingDns, launchEntriModal]);

  return {
    launchEntriModal,
    isLoadingEntri,
    entriLaunched,
    resetEntriLaunched: () => setEntriLaunched(false),
  };
};
